import { DropdownIndicator } from '@ui/DropdownIndicator';
import React, { useCallback, useMemo, useState } from 'react';

import { Button } from '@ui/buttons';
import { EllipsisText } from '@ui/EllipsisText';
import { Menu } from '@ui/menus/Menu';
import { EPDMenuContent } from '@features/epd/EPDMenu';
import { EpdSelectAction } from '@features/epd/EPDMenu/EPDMenuContent';
import {
  addIdToLocallyStoredEPDIds,
  useAppliedEpdsFromGenericProduct,
  useClearEpdOnProductElements,
  useSetEpdOnProductElements,
} from '@features/products/use-product-switch.hooks';
import {
  getEPDProductId,
  getGenericProductId,
} from '@shared/helpers/product-element.helpers';
import { guards } from '@shared/helpers/typeguards.helpers';
import { tryCatch } from '@shared/helpers/utils.helpers';
import { type ProductCategoryElement } from '@shared/models/project.model';
import { type IProductElement } from '@shared/models/project.model';
import { type ProductID } from '@shared/models/product.model';
import { useIsReadonly } from '@client/hooks/use-user.hooks';
import { useSelectedVersionProducts } from '@client/store/ui';

import { getEpdSelection } from '../product-bulk-edit-form.helpers';
import amplitudeLog from '@client/amplitude';
import {
  AMP_APP_OPEN_EPD_SELECTOR,
  AMP_PRODUCT_LIST_APPLY_EPD_TO_MULTIPLE,
} from '@shared/constants';

type BulkEditEpdSelectorProps = {
  /** Disables the trigger and prevents opening the menu. */
  disabled?: boolean;
  /** Product elements in scope for EPD selection and bulk apply/clear. */
  productElements: IProductElement[];
  /** Category element used as parent when creating a new EPD. */
  productCategoryElement?: ProductCategoryElement;
  onSelect?: (id: ProductID, genericId?: ProductID) => void;
};

export const BulkEditEpdSelector: React.FC<BulkEditEpdSelectorProps> = ({
  disabled = false,
  productElements,
  productCategoryElement,
  onSelect,
}) => {
  const readonly = useIsReadonly();
  const versionProducts = useSelectedVersionProducts();
  const epdSelection = useMemo(
    () => getEpdSelection(productElements),
    [productElements],
  );
  const genericIdsSet = useMemo(() => {
    const ids = productElements.map((element) => getGenericProductId(element));
    return new Set(ids);
  }, [productElements]);
  const singleGenericId = useMemo(
    () =>
      genericIdsSet.size !== 1 ? undefined : Array.from(genericIdsSet).at(0),
    [genericIdsSet],
  );
  const appliedEPD = useMemo(
    () => (epdSelection.id ? versionProducts?.[epdSelection.id] : undefined),
    [epdSelection.id, versionProducts],
  );
  const mappedEPDs = useAppliedEpdsFromGenericProduct(
    singleGenericId ?? '',
    appliedEPD,
  );
  const clearEPD = useClearEpdOnProductElements();
  const setEpdOnProductElements = useSetEpdOnProductElements();
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const selectedEpdIds = useMemo(() => {
    return productElements
      .map((element) => getEPDProductId(element))
      .filter(guards.is.defined);
  }, [productElements]);

  const selectedLabel = useMemo(() => {
    if (genericIdsSet.size > 1) return 'Mixed';
    if (epdSelection.mixed) return 'Mixed';
    if (epdSelection.id) {
      const selected = mappedEPDs.find((item) => item.id === epdSelection.id);
      if (selected?.name) return selected.name;
    }
    return 'Select EPD';
  }, [epdSelection.id, epdSelection.mixed, genericIdsSet, mappedEPDs]);

  const handleOpenMenu = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      if (readonly || disabled) return;
      setMenuAnchor(event.currentTarget);
      amplitudeLog(AMP_APP_OPEN_EPD_SELECTOR);
    },
    [disabled, readonly],
  );

  const handleCloseMenu = useCallback(() => {
    setMenuAnchor(null);
  }, []);

  const handleSelectProduct = useCallback(
    async (id: ProductID, genericId?: ProductID) => {
      await setEpdOnProductElements(productElements, id);

      onSelect?.(id, genericId);

      amplitudeLog(AMP_PRODUCT_LIST_APPLY_EPD_TO_MULTIPLE, {
        EpdId: id,
        ElementCount: productElements.length,
      });
    },
    [onSelect, productElements, setEpdOnProductElements],
  );

  const handleSelectEPD = useCallback(
    async (action: EpdSelectAction) => {
      switch (action.type) {
        case 'none': {
          await clearEPD(productElements);
          if (epdSelection.id && singleGenericId) {
            addIdToLocallyStoredEPDIds(singleGenericId, epdSelection.id);
          }
          if (singleGenericId) {
            onSelect?.(singleGenericId);
          }
          break;
        }
        case 'epd': {
          const result = tryCatch(() =>
            handleSelectProduct(action.id, singleGenericId),
          );
          if (!result.ok) return;
          break;
        }
      }
      handleCloseMenu();
    },
    [
      clearEPD,
      epdSelection.id,
      handleSelectProduct,
      onSelect,
      productElements,
      singleGenericId,
      handleCloseMenu,
    ],
  );
  return (
    <>
      <Button
        onClick={handleOpenMenu}
        variant="text-aligned"
        color="neutral"
        minWidth={0}
        disabled={disabled}
        textTransform="none"
        endIcon={<DropdownIndicator open={Boolean(menuAnchor)} />}
      >
        <EllipsisText>{selectedLabel}</EllipsisText>
      </Button>
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleCloseMenu}
        width="auto"
      >
        <EPDMenuContent
          epds={singleGenericId ? mappedEPDs : []}
          selectedEpdIds={selectedEpdIds}
          productCategoryElement={productCategoryElement}
          genericProductId={singleGenericId}
          onEpdSelect={handleSelectEPD}
        />
      </Menu>
    </>
  );
};
